import React, { useState, useEffect } from "react";
import {
  Avatar,
  Box,
  Dialog,
  DialogContent,
  Stack,
  Typography,
} from "@mui/material";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import TodayIcon from "@mui/icons-material/Today";
import DateRangeIcon from "@mui/icons-material/DateRange";
import InsightsIcon from "@mui/icons-material/Insights";
import GroupsIcon from "@mui/icons-material/Groups";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import Slide from "@mui/material/Slide";
import CircularLoader from "../../../components/loaders/circularLoader";
import DataTableWithCheckbox from "../../../components/dataTableWithSearch/DataTableWithCheckBox";
import { formatDate } from "../../../components/formatDateAndTime/formatDateAndTime";
import { QR, QrModalHeader, qrDialogPaperSx } from "./qrUi";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const num = (v) => Number(v) || 0;
const fmt = (n) => new Intl.NumberFormat("en-IN").format(num(n));

// Count pill (display only), same look as the list table
const CountPill = ({ value, color }) =>
  num(value) > 0 ? (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 32,
        px: 1,
        py: 0.25,
        borderRadius: 5,
        fontSize: 12,
        fontWeight: 700,
        color,
        bgcolor: `${color}14`,
        border: `1px solid ${color}33`,
      }}
    >
      {fmt(value)}
    </Box>
  ) : (
    <Typography variant="body2" color="text.disabled">
      0
    </Typography>
  );

export default function ViewQrCodeCounts({ show, handleClose, rowData }) {
  const [isLoading, setIsLoading] = useState(true);
  const rows = Array.isArray(rowData) ? rowData : [];

  const columns = [
    { header: "SNO", accessorKey: "sno", enableGlobalFilter: true },
    {
      accessorKey: "title",
      header: "QR Name",
      Cell: ({ row }) => (
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Avatar variant="rounded" sx={{ width: 28, height: 28, borderRadius: 1.5, bgcolor: `${QR.main}14`, color: QR.main }}>
            <QrCode2Icon sx={{ fontSize: 17 }} />
          </Avatar>
          <Typography variant="body2" fontWeight={700} color="#0f172a">
            {row.original.title}
          </Typography>
        </Stack>
      ),
    },
    {
      accessorKey: "url",
      header: "QR Key",
      Cell: ({ row }) =>
        row.original.url ? (
          <Box
            component="span"
            sx={{ px: 1, py: 0.25, borderRadius: 1.5, fontFamily: "monospace", fontSize: 12, color: "#334155", bgcolor: "#f1f5f9", border: "1px solid #e2e8f0" }}
          >
            {row.original.url}
          </Box>
        ) : "-",
    },
    { accessorKey: "today_count", header: "Today", Cell: ({ row }) => <CountPill value={row.original.today_count} color={QR.main} /> },
    { accessorKey: "yesterday_count", header: "Yesterday", Cell: ({ row }) => <CountPill value={row.original.yesterday_count} color={QR.blue} /> },
    { accessorKey: "last7days_count", header: "Last 7 Days", Cell: ({ row }) => <CountPill value={row.original.last7days_count} color={QR.purple} /> },
    { accessorKey: "last30days_count", header: "Last 30 Days", Cell: ({ row }) => <CountPill value={row.original.last30days_count} color={QR.amber} /> },
    { accessorKey: "overall_count", header: "Overall", Cell: ({ row }) => <CountPill value={row.original.overall_count} color="#0f172a" /> },
    {
      accessorKey: "inserted_time",
      header: "Created Time",
      Cell: ({ row }) => (
        <Typography variant="body2" color="text.secondary">
          {formatDate(row.original.inserted_time)}
        </Typography>
      ),
    },
  ];

  // Loader on modal open
  useEffect(() => {
    if (show) {
      setIsLoading(true);
      const t = setTimeout(() => setIsLoading(false), 1200);
      return () => clearTimeout(t);
    }
  }, [show]);

  // Summary across the compared QR codes (display only)
  const sum = (key) => rows.reduce((s, r) => s + num(r[key]), 0);
  const top = rows.reduce((best, r) => (!best || num(r.overall_count) > num(best.overall_count) ? r : best), null);
  const summary = [
    { label: "Today", value: sum("today_count"), icon: <TodayIcon />, color: QR.main },
    { label: "Last 7 Days", value: sum("last7days_count"), icon: <DateRangeIcon />, color: QR.purple },
    { label: "Last 30 Days", value: sum("last30days_count"), icon: <InsightsIcon />, color: QR.amber },
    { label: "Overall Leads", value: sum("overall_count"), icon: <GroupsIcon />, color: "#0f172a" },
  ];

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      TransitionComponent={Transition}
      fullWidth
      maxWidth="lg"
      keepMounted
      PaperProps={{ sx: { ...qrDialogPaperSx, maxWidth: 1060 } }} // a bit narrower than "lg" (1200px)
    >
      <QrModalHeader
        icon={<CompareArrowsIcon />}
        title="QR Comparison"
        subtitle={`Comparing ${rows.length} QR codes`}
        onClose={() => handleClose()}
      />

      <DialogContent sx={{ p: { xs: 2, md: 3 }, bgcolor: "#fff" }}>
        {isLoading ? (
          <CircularLoader />
        ) : (
          <>
            {/* Summary strip */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))", md: "repeat(5, minmax(0, 1fr))" },
                gap: 1.5,
                mb: 2,
              }}
            >
              {summary.map((s) => (
                <Stack
                  key={s.label}
                  direction="row"
                  alignItems="center"
                  spacing={1.25}
                  sx={{
                    p: 1.25,
                    borderRadius: 2.5,
                    bgcolor: "#fff",
                    border: `1px solid ${QR.border}`,
                    borderTop: `3px solid ${s.color}`,
                  }}
                >
                  <Avatar variant="rounded" sx={{ width: 34, height: 34, borderRadius: 2, color: s.color, bgcolor: `${s.color}14` }}>
                    {s.icon}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} noWrap display="block">
                      {s.label}
                    </Typography>
                    <Typography fontWeight={800} fontSize={18} lineHeight={1.2}>
                      {fmt(s.value)}
                    </Typography>
                  </Box>
                </Stack>
              ))}

              {/* Top performer */}
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.25}
                sx={{
                  p: 1.25,
                  borderRadius: 2.5,
                  bgcolor: "#fffbeb",
                  border: "1px solid #fde68a",
                  borderTop: "3px solid #f59e0b",
                  gridColumn: { xs: "span 2", sm: "span 2", md: "auto" },
                  minWidth: 0,
                }}
              >
                <Avatar variant="rounded" sx={{ width: 34, height: 34, borderRadius: 2, color: "#d97706", bgcolor: "#fef3c7" }}>
                  <EmojiEventsIcon />
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="#92400e" fontWeight={600} noWrap display="block">
                    Top performer
                  </Typography>
                  <Typography fontWeight={800} fontSize={15} lineHeight={1.25} noWrap title={top?.title}>
                    {top && num(top.overall_count) > 0 ? `${top.title} · ${fmt(top.overall_count)}` : "—"}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {/* Same modern table as the QR list (search on the left) */}
            <DataTableWithCheckbox
              columns={columns}
              data={rows}
              enableRowSelection={false}
              searchFirst
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
